/**
 * Autenticación vía bcknd-auth (patrón token handler, modelo híbrido).
 *
 * El navegador hace el redirect a Keycloak con PKCE, pero el canje del code y el refresh
 * los hace bcknd-auth (client confidential), que deja el refresh token en una cookie
 * HttpOnly cifrada — nunca accesible desde JavaScript. El access token vive SOLO en
 * memoria: tras un F5 se restaura con un refresh silencioso contra la cookie.
 *
 * Los endpoints de Keycloak se construyen estáticamente por realm (sin discovery
 * `.well-known`). bcknd-auth vive detrás del mismo balanceador que el resto de la API,
 * así que comparte `VITE_API_BASE_URL`; `VITE_AUTH_BASE_URL` solo se define (vacía) en
 * `.env.fax-localhost` para que `/auth` vaya relativo al proxy de Vite — la cookie
 * HttpOnly exige same-origin y `localhost → services-dev` es cross-site.
 */

/** Estado propio del round-trip por Keycloak, persistido en sessionStorage bajo el `state`. */
interface AuthState {
  returnTo: string
  verifier?: string
}

interface TokenResponse {
  access_token: string
  expires_in: number
  id_token?: string
}

// Header anti-CSRF con el client_id OIDC ("oidc-client" para no confundir con el
// "cliente" del dominio del portal, que viaja en x-ax-workspace).
const CLIENT_HEADER = 'x-ax-oidc-client'
const STORAGE_PREFIX = 'sup-auth:'
const RETURN_TO_KEY = STORAGE_PREFIX + 'return-to'
// Marca que ya se disparó un login para recuperar un callback incompleto: evita ciclar.
const RECOVERY_KEY = STORAGE_PREFIX + 'recovery'
const EXPIRY_MARGIN_MS = 10_000

const apiBase = import.meta.env.VITE_API_BASE_URL
const authBase = import.meta.env.VITE_AUTH_BASE_URL ?? apiBase
const realm = import.meta.env.VITE_OIDC_REALM
const clientId = import.meta.env.VITE_OIDC_CLIENT_ID

const keycloakBase = `${apiBase}/login/realms/${realm}/protocol/openid-connect`
const callbackPath = `${import.meta.env.BASE_URL}callback`
const redirectUri = `${window.location.origin}${callbackPath}`

/**
 * True si la página actual es el callback OIDC. Ahí no hay sesión previa que restaurar —la
 * crea el canje del code—, así que un refresh preventivo sería un 401 garantizado.
 */
export function isCallbackPath(): boolean {
  return window.location.pathname === callbackPath
}

let accessToken: string | null = null
let accessTokenExpiresAt = 0
let idToken: string | null = null
let refreshPromise: Promise<string | null> | null = null
let refreshTimer: number | null = null
let onScheduledRefresh: (() => void) | null = null

/**
 * Callback invocado tras cada refresh programado exitoso (no en el login ni en los
 * refresh bajo demanda, que ya tienen su propio manejo). El store lo usa para
 * mantener fresco el userinfo.
 */
export function setOnScheduledRefresh(handler: () => void): void {
  onScheduledRefresh = handler
}

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = ''
  bytes.forEach((b) => (binary += String.fromCharCode(b)))
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function randomUrlSafe(byteLength: number): string {
  return base64UrlEncode(crypto.getRandomValues(new Uint8Array(byteLength)))
}

async function computeCodeChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))
  return base64UrlEncode(new Uint8Array(digest))
}

function saveState(state: string, value: AuthState): void {
  sessionStorage.setItem(STORAGE_PREFIX + state, JSON.stringify(value))
  // Breadcrumb para el caso de recuperación: sobrevive al consumo one-shot del state.
  sessionStorage.setItem(RETURN_TO_KEY, value.returnTo)
}

function takeState(state: string): AuthState | null {
  const key = STORAGE_PREFIX + state
  const stored = sessionStorage.getItem(key)
  sessionStorage.removeItem(key)
  return stored ? (JSON.parse(stored) as AuthState) : null
}

function storeTokens(body: TokenResponse): void {
  accessToken = body.access_token
  accessTokenExpiresAt = Date.now() + body.expires_in * 1000
  idToken = body.id_token ?? idToken
  scheduleRefresh(body.expires_in)
}

function clearTokens(): void {
  accessToken = null
  accessTokenExpiresAt = 0
  idToken = null
  cancelScheduledRefresh()
}

/**
 * Refresh proactivo: renueva poco antes de que expire el access token (intervalo tomado
 * del `expires_in` de la respuesta), en vez de esperar al próximo uso. El chequeo lazy de
 * getAccessToken() queda como respaldo (p. ej. si el equipo durmió y el timer llegó tarde).
 */
function scheduleRefresh(expiresInSeconds: number): void {
  cancelScheduledRefresh()
  const delay = Math.max(expiresInSeconds * 1000 - EXPIRY_MARGIN_MS, 5_000)
  refreshTimer = window.setTimeout(async () => {
    refreshTimer = null
    const token = await refreshAccessToken()
    if (token && onScheduledRefresh) {
      onScheduledRefresh()
    }
    // si falló, clearTokens() ya cortó la cadena; el próximo uso hará el 401 → login
  }, delay)
}

function cancelScheduledRefresh(): void {
  if (refreshTimer !== null) {
    clearTimeout(refreshTimer)
    refreshTimer = null
  }
}

/**
 * Redirige al authorization endpoint con PKCE. `kcAction` dispara una Application Initiated
 * Action de Keycloak (p. ej. `UPDATE_PASSWORD`): mismo flujo de autorización, pero Keycloak
 * intercala el formulario de la acción antes de volver al callback con un code nuevo.
 */
async function authorize(returnTo: string, kcAction?: string): Promise<void> {
  const state = randomUrlSafe(16)
  const verifier = randomUrlSafe(48)
  saveState(state, { returnTo, verifier })

  const params = new URLSearchParams({
    client_id: clientId,
    response_type: 'code',
    scope: 'openid',
    redirect_uri: redirectUri,
    state,
    code_challenge: await computeCodeChallenge(verifier),
    code_challenge_method: 'S256',
  })
  if (kcAction) {
    params.set('kc_action', kcAction)
  }
  window.location.assign(`${keycloakBase}/auth?${params}`)
}

export function login(returnTo: string): Promise<void> {
  return authorize(returnTo)
}

/**
 * Abre el formulario de cambio de contraseña de Keycloak y vuelve a `returnTo`. Si el usuario
 * cancela, Keycloak vuelve igual al callback (con `kc_action_status=cancelled`) y la sesión
 * sigue vigente, así que el retorno es el mismo en ambos casos.
 */
export function updatePassword(returnTo: string): Promise<void> {
  return authorize(returnTo, 'UPDATE_PASSWORD')
}

/**
 * Completa el callback de login y devuelve la ruta a restaurar, o `null` cuando el callback
 * no era canjeable en esta pestaña y se disparó un login nuevo (la página ya está navegando
 * a Keycloak, el caller no debe hacer nada más).
 */
export async function handleLoginCallback(): Promise<string | null> {
  const params = new URLSearchParams(window.location.search)

  const error = params.get('error')
  if (error) {
    throw new Error(params.get('error_description') ?? error)
  }

  const code = params.get('code')
  const state = params.get('state')
  if (!code || !state) {
    throw new Error('Callback de login sin code/state')
  }

  const authState = takeState(state)
  let exchangeStatus: number | null = null

  if (authState?.verifier) {
    // Formato estándar del token endpoint OAuth (RFC 6749): URLSearchParams pone solo
    // el Content-Type application/x-www-form-urlencoded.
    const response = await fetch(`${authBase}/auth/token`, {
      method: 'POST',
      credentials: 'include',
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: clientId,
        code,
        code_verifier: authState.verifier,
        redirect_uri: redirectUri,
      }),
    })
    if (response.ok) {
      storeTokens((await response.json()) as TokenResponse)
      sessionStorage.removeItem(RETURN_TO_KEY)
      sessionStorage.removeItem(RECOVERY_KEY)
      return authState.returnTo || '/'
    }
    exchangeStatus = response.status
  }

  // El code pudo haberse canjeado ya en una carga anterior de esta misma página (recarga
  // de Vite en dev, F5 del usuario en medio del callback): el state se consumió y/o
  // Keycloak rechaza el code repetido, pero la cookie ya tiene la sesión. Se intenta
  // recuperar con un refresh silencioso antes de darlo por perdido.
  const token = await getAccessToken()
  if (token) {
    const returnTo = authState?.returnTo || sessionStorage.getItem(RETURN_TO_KEY)
    sessionStorage.removeItem(RETURN_TO_KEY)
    sessionStorage.removeItem(RECOVERY_KEY)
    return returnTo || '/'
  }

  // Callback que esta pestaña no puede canjear: el flujo empezó en otra, así que no están
  // aquí el state ni el verifier PKCE (sessionStorage es por pestaña). Pasa con el link de
  // "olvidé mi contraseña", que Keycloak envía por correo y se abre en una pestaña nueva:
  // al terminar el cambio, la sesión SSO ya existe, así que un login nuevo vuelve de
  // inmediato con un code canjeable aquí. Una sola vez, para no ciclar si el canje falla
  // por otra razón (p. ej. bcknd-auth caído).
  if (!sessionStorage.getItem(RECOVERY_KEY)) {
    sessionStorage.setItem(RECOVERY_KEY, '1')
    // Sin returnTo propio: la raíz sigue al último cliente usado.
    await login(sessionStorage.getItem(RETURN_TO_KEY) || '/')
    return null
  }
  sessionStorage.removeItem(RECOVERY_KEY)

  throw new Error(
    exchangeStatus !== null
      ? `El canje del code falló: HTTP ${exchangeStatus}`
      : 'State desconocido en el callback de login',
  )
}

/**
 * Cierra la sesión: revoca el refresh token en bcknd-auth (borra la cookie) y redirige a
 * end_session de Keycloak para matar también la sesión SSO del navegador. `id_token_hint`
 * evita la pantalla de confirmación; si no hay id_token en memoria (p. ej. 401 tras F5
 * sin refresh previo), Keycloak la muestra y el flujo sigue igual.
 */
export async function logout(returnTo: string): Promise<void> {
  const state = randomUrlSafe(16)
  saveState(state, { returnTo })

  const hint = idToken
  try {
    await fetch(`${authBase}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: { [CLIENT_HEADER]: clientId },
    })
  } catch (err) {
    console.error('La revocación de la sesión falló; se continúa con el logout SSO:', err)
  }
  clearTokens()

  const params = new URLSearchParams({
    client_id: clientId,
    post_logout_redirect_uri: redirectUri,
    state,
  })
  if (hint) {
    params.set('id_token_hint', hint)
  }
  window.location.assign(`${keycloakBase}/logout?${params}`)
}

export async function handleLogoutCallback(): Promise<string> {
  const state = new URLSearchParams(window.location.search).get('state')
  const authState = state ? takeState(state) : null
  return authState?.returnTo || '/'
}

/**
 * Access token vigente, renovando contra la cookie si expiró o no hay (F5). Devuelve null
 * si no hay sesión recuperable — el caller decide redirigir a login.
 */
export async function getAccessToken(): Promise<string | null> {
  if (accessToken && Date.now() < accessTokenExpiresAt - EXPIRY_MARGIN_MS) {
    return accessToken
  }
  return refreshAccessToken()
}

/** Single-flight: un solo refresh concurrente; los demás callers esperan el mismo resultado. */
function refreshAccessToken(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = doRefresh().finally(() => {
      refreshPromise = null
    })
  }
  return refreshPromise
}

async function doRefresh(): Promise<string | null> {
  try {
    const response = await fetch(`${authBase}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { [CLIENT_HEADER]: clientId },
    })
    if (!response.ok) {
      clearTokens()
      return null
    }
    storeTokens((await response.json()) as TokenResponse)
    return accessToken
  } catch (err) {
    console.error('El refresh del token falló:', err)
    clearTokens()
    return null
  }
}
